import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiFilter } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import MobilePageBar, { NewPostButton } from '../components/MobilePageBar';
import './PostsLive.css';

const FilterIcon = FiFilter as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'published', label: 'Published' },
  { value: 'publishing', label: 'Publishing' },
  { value: 'queued', label: 'Queued' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface ProjectOption {
  id: string;
  name: string;
}

interface LivePostPerformance {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagementRate: number;
}

interface LivePost {
  id: string;
  publishRequestId: string;
  linkedAccountId: string | null;
  status: string;
  error: string | null;
  projectId: string | null;
  projectName: string | null;
  videoId: string;
  shortId: string | null;
  title: string | null;
  caption: string | null;
  hashtags: string[];
  scheduleType: string;
  scheduledTime: string | null;
  timezone: string | null;
  platform: string;
  platformPostId: string | null;
  mediaType: string;
  accountName: string;
  accountEmail: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  postUrl: string | null;
  hasInsights: boolean;
  insightsFetchedAt: string | null;
  performance: LivePostPerformance;
}

interface LivePostsResponse {
  count: number;
  status: string;
  projectId: string;
  statusCounts: Record<string, number>;
  posts: LivePost[];
}

function formatStatus(status: string): string {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
}

function statusClass(status: string): string {
  if (status === 'published') return 'posts-live-status-pill--published';
  if (status === 'publishing') return 'posts-live-status-pill--publishing';
  if (status === 'scheduled') return 'posts-live-status-pill--scheduled';
  if (status === 'queued') return 'posts-live-status-pill--queued';
  if (status === 'failed') return 'posts-live-status-pill--failed';
  if (status === 'cancelled') return 'posts-live-status-pill--cancelled';
  return 'posts-live-status-pill--default';
}

function formatPlatform(platform: string): string {
  if (platform === 'twitter') return 'X';
  if (platform === 'youtube') return 'YouTube';
  if (platform === 'tiktok') return 'TikTok';
  if (platform === 'facebook') return 'Facebook';
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'instagram') return 'Instagram';
  return platform;
}

function formatMetric(value: number): string {
  if (value >= 1000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US').format(value);
}

function formatPublishedAt(value: string | null): string {
  if (!value) return 'Published time unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Published time unavailable';
  return date.toLocaleString();
}

function formatScheduleLabel(post: LivePost): string {
  if (post.scheduleType === 'scheduled') {
    const scheduled = formatPublishedAt(post.scheduledTime || post.createdAt);
    return `${scheduled}${post.timezone ? ` (${post.timezone})` : ''}`;
  }

  if (!post.scheduleType) {
    return 'Immediate';
  }

  return post.scheduleType.charAt(0).toUpperCase() + post.scheduleType.slice(1);
}

const PostsLive: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState<LivePost[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('published');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [selectedPost, setSelectedPost] = useState<LivePost | null>(null);
  // Below 768px the Project/Status filters live in a left overlay drawer (see responsive.css).
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFiltersOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [filtersOpen]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);
  const [refreshingPostId, setRefreshingPostId] = useState<string | null>(null);

  const openInsights = useCallback((post: LivePost) => {
    const fallbackVideoId = post.platformPostId || post.videoId;
    const params = new URLSearchParams();
    if (fallbackVideoId) {
      params.set('videoId', fallbackVideoId);
    }

    const insightsBasePath = location.pathname.startsWith('/analytics/insights')
      ? '/analytics/insights'
      : '/posts/live';

    navigate(`${insightsBasePath}/${post.id}${params.toString() ? `?${params.toString()}` : ''}`, {
      state: { post },
    });
  }, [location.pathname, navigate]);

  const fetchProjects = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch(`${API_BASE}/api/projects`, {
        credentials: 'include',
        signal,
      });
      if (!response.ok) return;
      const payload = await response.json() as ProjectOption[];
      if (Array.isArray(payload)) {
        const normalized = payload
          .filter((p) => p && typeof p.id === 'string' && typeof p.name === 'string')
          .map((p) => ({ id: p.id, name: p.name }));
        setProjects(normalized);
      }
    } catch {
      // Best-effort only; the posts list can still render without project options.
    }
  }, []);

  const fetchLivePosts = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (projectFilter !== 'all') params.set('projectId', projectFilter);

      const response = await fetch(`${API_BASE}/api/posts/live${params.toString() ? `?${params.toString()}` : ''}`, {
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload.error || 'Failed to load live posts');
      }

      const payload = await response.json() as LivePostsResponse;
      setPosts(Array.isArray(payload.posts) ? payload.posts : []);
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load live posts');
    } finally {
      setLoading(false);
    }
  }, [projectFilter, statusFilter]);

  const handleRefreshPostMetrics = useCallback(async (post: LivePost) => {
    if (!post.linkedAccountId) {
      setError('This post is not linked to an account yet.');
      return;
    }
    if (!post.platformPostId) {
      setError('This post is missing a platform post ID, so post-level refresh is unavailable.');
      return;
    }

    setError(null);
    setRefreshNotice(null);
    setRefreshingPostId(post.id);

    try {
      const payload: Record<string, unknown> = {
        force: true,
        reason: 'live-posts-post-refresh',
        maxCandidates: 1,
        platformPostId: post.platformPostId,
      };

      const response = await fetch(`${API_BASE}/api/accounts/${post.linkedAccountId}/insights/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({} as { error?: string; acceptedCandidates?: number }));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to queue metrics refresh');
      }

      const accepted = typeof data.acceptedCandidates === 'number' ? data.acceptedCandidates : null;
      setRefreshNotice(
        accepted === 0
          ? 'No eligible candidate found for this post right now.'
          : 'Post metrics refresh queued.'
      );

      await fetchLivePosts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to queue metrics refresh');
    } finally {
      setRefreshingPostId(null);
    }
  }, [fetchLivePosts]);

  useEffect(() => {
    const controller = new AbortController();
    fetchLivePosts(controller.signal);
    return () => controller.abort();
  }, [fetchLivePosts]);

  useEffect(() => {
    const controller = new AbortController();
    fetchProjects(controller.signal);
    return () => controller.abort();
  }, [fetchProjects]);

  useEffect(() => {
    if (!selectedPost) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedPost(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedPost]);

  const avgEngagementRate = useMemo(() => {
    if (posts.length === 0) return 0;
    const total = posts.reduce((sum, post) => sum + post.performance.engagementRate, 0);
    return Number((total / posts.length).toFixed(2));
  }, [posts]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <MobilePageBar current="Live" label="🟢 Live">
          <button
            type="button"
            className="mobile-page-btn"
            onClick={() => setFiltersOpen(true)}
            title="Filters"
            aria-label="Filters"
            aria-expanded={filtersOpen}
          >
            <FilterIcon aria-hidden={true} />
          </button>
        </MobilePageBar>

        <div className="page-header posts-list-header">
          <div>
            <h1>Live Posts</h1>
            <p className="page-subtitle">Publish records with performance and status tracking</p>
          </div>
        </div>

        <div className={`posts-live-filters-drawer${filtersOpen ? ' posts-live-filters-drawer--open' : ''}`}>
        <div className="posts-live-filters-drawer-head">
          <span className="posts-live-filters-drawer-title">Filters</span>
          <button type="button" className="posts-live-filters-drawer-close" onClick={() => setFiltersOpen(false)} aria-label="Close filters">✕</button>
        </div>
        <div className="posts-live-filters">
          <label className="posts-live-filter-group">
            <span>Project</span>
            <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>

          <label className="posts-live-filter-group">
            <span>Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((statusOption) => (
                <option key={statusOption.value} value={statusOption.value}>{statusOption.label}</option>
              ))}
            </select>
          </label>
        </div>
        </div>
        <div className={`posts-live-filters-backdrop${filtersOpen ? ' is-open' : ''}`} onClick={() => setFiltersOpen(false)} aria-hidden="true" />


        <div className="posts-live-summary-row">
          <div className="posts-live-summary-card">
            <div className="posts-live-summary-label">Records</div>
            <div className="posts-live-summary-value">{posts.length}</div>
          </div>
          <div className="posts-live-summary-card">
            <div className="posts-live-summary-label">Avg Engagement Rate</div>
            <div className="posts-live-summary-value">{avgEngagementRate}%</div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button className="posts-live-retry-btn" onClick={() => fetchLivePosts()}>
              Retry
            </button>
          </div>
        )}

        {refreshNotice && (
          <div className="posts-live-success-banner">
            <span>{refreshNotice}</span>
          </div>
        )}

        {loading ? (
          <div className="posts-live-empty-state">
            <div className="posts-live-empty-icon">⏳</div>
            <p>Loading publish records...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="posts-live-empty-state">
            <div className="posts-live-empty-icon">🟢</div>
            <p>No records found for this filter.</p>
            <p className="posts-live-empty-note">Try changing project or status filters.</p>
          </div>
        ) : (
          <div className="posts-live-list">
            {posts.map((post, index) => (
              <article
                key={post.id}
                className="posts-live-item posts-live-item--clickable"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedPost(post)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedPost(post);
                  }
                }}
              >
                <div className="posts-live-post-number">#{index + 1}</div>

                <div className="posts-live-post-main">
                  <div className="posts-live-post-top">
                    <h3 className="posts-live-post-title">
                      {post.title || `${formatPlatform(post.platform)} post`}
                    </h3>
                    <div className="posts-live-badges">
                      <span className={`posts-live-status-pill ${statusClass(post.status)}`}>
                        {formatStatus(post.status)}
                      </span>
                      <span className="posts-live-platform-pill">{formatPlatform(post.platform)}</span>
                    </div>
                  </div>

                  <div className="posts-live-post-meta">
                    <span>Title: {post.title || 'Untitled'}</span>
                    <span>Project: {post.projectName || 'No Project'}</span>
                    <span>Account: {post.accountName}</span>
                    <span>Posted: {formatPublishedAt(post.publishedAt || post.createdAt)}</span>
                    <span>Schedule: {formatScheduleLabel(post)}</span>
                    <span>Type: {post.mediaType}</span>
                  </div>

                  {post.caption && (
                    <p className="posts-live-post-description">{post.caption}</p>
                  )}

                  {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
                    <div className="posts-live-post-tags">{post.hashtags.map((tag) => `#${tag}`).join(' ')}</div>
                  )}

                  <div className="posts-live-performance-grid">
                    <div className="posts-live-performance-item">
                      <span>Views</span>
                      <strong>{post.hasInsights ? formatMetric(post.performance.views) : '—'}</strong>
                    </div>
                    <div className="posts-live-performance-item">
                      <span>Likes</span>
                      <strong>{post.hasInsights ? formatMetric(post.performance.likes) : '—'}</strong>
                    </div>
                    <div className="posts-live-performance-item">
                      <span>Comments</span>
                      <strong>{post.hasInsights ? formatMetric(post.performance.comments) : '—'}</strong>
                    </div>
                    <div className="posts-live-performance-item">
                      <span>Shares</span>
                      <strong>{post.hasInsights ? formatMetric(post.performance.shares) : '—'}</strong>
                    </div>
                    <div className="posts-live-performance-item">
                      <span>Engagement</span>
                      <strong>{post.hasInsights ? formatMetric(post.performance.engagement) : '—'}</strong>
                    </div>
                    <div className="posts-live-performance-item">
                      <span>Rate</span>
                      <strong>{post.hasInsights ? `${post.performance.engagementRate}%` : '—'}</strong>
                    </div>
                  </div>

                  {!post.hasInsights && (
                    <div className="posts-live-performance-note">Insights not synced yet. Click refresh to fetch latest metrics.</div>
                  )}
                </div>

                <div className="posts-live-item-actions" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className="posts-live-icon-action-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleRefreshPostMetrics(post);
                    }}
                    disabled={refreshingPostId === post.id}
                    aria-label={refreshingPostId === post.id ? 'Refreshing post metrics' : 'Refresh post metrics'}
                    title={refreshingPostId === post.id
                      ? 'Refreshing post metrics'
                      : (!post.linkedAccountId
                        ? 'Refresh unavailable: account is not linked'
                        : (!post.platformPostId
                          ? 'Refresh unavailable: platform post ID is missing'
                          : 'Refresh post metrics'))}
                  >
                    {refreshingPostId === post.id ? '⏳' : '↻'}
                  </button>

                  <button
                    type="button"
                    className="posts-live-link posts-live-link--insights"
                    onClick={(event) => {
                      event.stopPropagation();
                      openInsights(post);
                    }}
                  >
                    Insights
                  </button>

                  {post.postUrl && (
                    <a
                      className="posts-live-link"
                      href={post.postUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Open
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {selectedPost && (
          <div className="posts-live-modal-overlay" onClick={() => setSelectedPost(null)}>
            <div className="posts-live-modal" onClick={(event) => event.stopPropagation()}>
              <div className="posts-live-modal-header">
                <div>
                  <h2>{selectedPost.title || `${formatPlatform(selectedPost.platform)} post`}</h2>
                  <p>Publish record details</p>
                </div>
                <button className="posts-live-modal-close" onClick={() => setSelectedPost(null)}>
                  Close
                </button>
              </div>

              <div className="posts-live-modal-grid">
                <div className="posts-live-modal-item">
                  <span>Status</span>
                  <strong className={`posts-live-status-pill ${statusClass(selectedPost.status)}`}>
                    {formatStatus(selectedPost.status)}
                  </strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Project</span>
                  <strong>{selectedPost.projectName || 'No Project'}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Platform</span>
                  <strong>{formatPlatform(selectedPost.platform)}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Account</span>
                  <strong>{selectedPost.accountName}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Published At</span>
                  <strong>{formatPublishedAt(selectedPost.publishedAt || selectedPost.createdAt)}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Description</span>
                  <strong>{selectedPost.caption || 'n/a'}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Hashtags</span>
                  <strong>{selectedPost.hashtags.length > 0 ? selectedPost.hashtags.map((tag) => `#${tag}`).join(' ') : 'n/a'}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Schedule</span>
                  <strong>{formatScheduleLabel(selectedPost)}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Insights Updated</span>
                  <strong>{selectedPost.insightsFetchedAt ? formatPublishedAt(selectedPost.insightsFetchedAt) : 'n/a'}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Record ID</span>
                  <strong className="posts-live-mono">{selectedPost.id}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Publish Request ID</span>
                  <strong className="posts-live-mono">{selectedPost.publishRequestId}</strong>
                </div>
                <div className="posts-live-modal-item">
                  <span>Platform Post ID</span>
                  <strong className="posts-live-mono">{selectedPost.platformPostId || 'n/a'}</strong>
                </div>
              </div>

              {selectedPost.error && (
                <div className="posts-live-modal-error">{selectedPost.error}</div>
              )}

              <div className="posts-live-performance-grid posts-live-performance-grid--modal">
                <div className="posts-live-performance-item">
                  <span>Views</span>
                  <strong>{selectedPost.hasInsights ? formatMetric(selectedPost.performance.views) : '—'}</strong>
                </div>
                <div className="posts-live-performance-item">
                  <span>Likes</span>
                  <strong>{selectedPost.hasInsights ? formatMetric(selectedPost.performance.likes) : '—'}</strong>
                </div>
                <div className="posts-live-performance-item">
                  <span>Comments</span>
                  <strong>{selectedPost.hasInsights ? formatMetric(selectedPost.performance.comments) : '—'}</strong>
                </div>
                <div className="posts-live-performance-item">
                  <span>Shares</span>
                  <strong>{selectedPost.hasInsights ? formatMetric(selectedPost.performance.shares) : '—'}</strong>
                </div>
                <div className="posts-live-performance-item">
                  <span>Engagement</span>
                  <strong>{selectedPost.hasInsights ? formatMetric(selectedPost.performance.engagement) : '—'}</strong>
                </div>
                <div className="posts-live-performance-item">
                  <span>Rate</span>
                  <strong>{selectedPost.hasInsights ? `${selectedPost.performance.engagementRate}%` : '—'}</strong>
                </div>
              </div>

              {!selectedPost.hasInsights && (
                <div className="posts-live-performance-note">Insights not synced yet. Use refresh to pull post analytics.</div>
              )}

              <div className="posts-live-modal-actions">
                <button
                  type="button"
                  className="posts-live-action-btn"
                  onClick={() => void handleRefreshPostMetrics(selectedPost)}
                  disabled={refreshingPostId === selectedPost.id}
                >
                  {refreshingPostId === selectedPost.id ? 'Refreshing post...' : 'Refresh post metrics'}
                </button>

                <button
                  type="button"
                  className="posts-live-action-btn posts-live-action-btn--secondary"
                  onClick={() => openInsights(selectedPost)}
                >
                  Open Insights
                </button>

                {selectedPost.postUrl && (
                  <a className="posts-live-link" href={selectedPost.postUrl} target="_blank" rel="noreferrer">
                    Open on Platform
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PostsLive;
